import type { ReactNode } from "react";
import { Mail, MapPin, Phone } from "lucide-react";
import { COMPANION_FIELD_ROWS, type CompanionFieldKey } from "@/lib/companionFields";
import { guestDisplayName, formatGuestFieldValue } from "@/lib/inquiryDisplay";
import { PlaceBanBadge } from "@/components/bookings/PlaceBanNotice";
import { guestTypeLabel } from "@/lib/guestType";
import type { Booking, Guest } from "@/types/camping";

const ADDRESS_KEYS = new Set<CompanionFieldKey>([
  "street",
  "house_number",
  "address_line_2",
  "postal_code",
  "city",
  "state_region",
  "country_code",
]);

const NAME_KEYS = new Set<CompanionFieldKey>([
  "title",
  "first_name",
  "middle_name",
  "last_name",
  "company_name",
]);

const CONTACT_KEYS = new Set<CompanionFieldKey>(["email", "phone"]);

function filled(value: string | null | undefined): string | null {
  const text = value?.trim();
  return text ? text : null;
}

function guestAddressLines(guest: Guest): string[] {
  const street = [filled(guest.street), filled(guest.house_number)].filter(Boolean).join(" ");
  const locality = [filled(guest.postal_code), filled(guest.city)].filter(Boolean).join(" ");
  const cityLine = [locality, filled(guest.state_region)].filter(Boolean).join(", ");
  const last = [cityLine, filled(guest.country_code)].filter(Boolean).join(" · ");
  return [street, filled(guest.address_line_2), last].filter((line): line is string => Boolean(line));
}

function ContactRow({
  icon,
  label,
  children,
}: {
  icon: ReactNode;
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="flex min-w-0 items-center gap-3">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-fill-tertiary text-label-secondary">
        {icon}
      </span>
      <div className="min-w-0">
        <dt className="text-[11px] font-medium uppercase tracking-wide text-label-tertiary">{label}</dt>
        <dd className="break-words text-sm font-medium text-label">{children}</dd>
      </div>
    </div>
  );
}

export interface GuestInquirySummaryProps {
  guest: Guest | null;
  booking: Booking;
}

export function GuestInquirySummary({ guest, booking }: GuestInquirySummaryProps) {
  const displayName = guestDisplayName(guest, booking.guest_name);
  const banned = Boolean(guest?.is_place_banned || booking.place_ban_flagged);
  const email = filled(guest?.email);
  const phone = filled(guest?.phone);
  const mobile = filled(guest?.phone_mobile);
  const showMobile = Boolean(mobile && mobile !== phone);
  const addressLines = guest ? guestAddressLines(guest) : [];
  const company = filled(guest?.company_name);

  const meta = guest
    ? COMPANION_FIELD_ROWS.flatMap((field) => {
        if (NAME_KEYS.has(field.key) || CONTACT_KEYS.has(field.key) || ADDRESS_KEYS.has(field.key)) {
          return [];
        }
        const raw = guest[field.key as keyof Guest];
        if (raw == null || (typeof raw === "string" && !raw.trim())) return [];
        return [
          {
            key: field.key,
            label: field.label,
            value: formatGuestFieldValue(field.key, String(raw)),
          },
        ];
      })
    : [];

  return (
    <section
      className={
        banned
          ? "rounded-xl border-2 border-status-red/40 bg-status-red/8 p-4 sm:p-5"
          : "rounded-xl border border-separator bg-fill-tertiary/30 p-4 sm:p-5"
      }
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-label-tertiary">Gast</p>
          <h3 className="mt-1 text-base font-semibold tracking-tight text-label">{displayName}</h3>
          {company ? <p className="mt-0.5 text-sm text-label-secondary">{company}</p> : null}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {guest?.guest_type ? (
            <span className="rounded-full bg-fill-tertiary px-2.5 py-0.5 text-xs font-medium text-label-secondary">
              {guestTypeLabel(guest.guest_type)}
            </span>
          ) : null}
          {guest?.is_place_banned ? <PlaceBanBadge prominent /> : null}
          {booking.place_ban_flagged ? (
            <span className="rounded-md border border-status-red/40 bg-status-red/15 px-2.5 py-0.5 text-xs font-bold uppercase tracking-wide text-status-red">
              Abgleich Platzverbot
            </span>
          ) : null}
        </div>
      </div>

      {email || phone || showMobile ? (
        <dl className="mt-4 grid gap-3 sm:grid-cols-2">
          {email ? (
            <ContactRow icon={<Mail className="h-4 w-4" aria-hidden />} label="E-Mail">
              <a href={`mailto:${email}`} className="hover:text-accent hover:underline">
                {email}
              </a>
            </ContactRow>
          ) : null}
          {phone ? (
            <ContactRow icon={<Phone className="h-4 w-4" aria-hidden />} label="Telefon">
              <a href={`tel:${phone.replace(/\s/g, "")}`} className="hover:text-accent hover:underline">
                {phone}
              </a>
            </ContactRow>
          ) : null}
          {showMobile && mobile ? (
            <ContactRow icon={<Phone className="h-4 w-4" aria-hidden />} label="Mobil">
              <a href={`tel:${mobile.replace(/\s/g, "")}`} className="hover:text-accent hover:underline">
                {mobile}
              </a>
            </ContactRow>
          ) : null}
        </dl>
      ) : null}

      {addressLines.length > 0 ? (
        <div className="mt-3 flex items-start gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-fill-tertiary text-label-secondary">
            <MapPin className="h-4 w-4" aria-hidden />
          </span>
          <div>
            <p className="text-[11px] font-medium uppercase tracking-wide text-label-tertiary">Adresse</p>
            {addressLines.map((line, index) => (
              <p key={`${line}-${index}`} className="text-sm font-medium text-label">
                {line}
              </p>
            ))}
          </div>
        </div>
      ) : null}

      {meta.length > 0 ? (
        <dl className="mt-4 grid gap-x-6 gap-y-2 border-t border-separator pt-3 sm:grid-cols-3">
          {meta.map((item) => (
            <div key={item.key}>
              <dt className="text-[11px] font-medium uppercase tracking-wide text-label-tertiary">
                {item.label}
              </dt>
              <dd className="text-sm font-medium text-label">{item.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}

      {guest?.is_place_banned && guest.place_ban_reason ? (
        <div className="mt-3 rounded-lg border border-status-red/30 bg-status-red/10 px-3 py-2 text-sm text-status-red">
          <span className="font-bold">Platzverbot: </span>
          {guest.place_ban_reason}
        </div>
      ) : null}

      {booking.place_ban_flagged && booking.place_ban_reason && !guest?.is_place_banned ? (
        <div className="mt-3 rounded-lg border border-status-red/30 bg-status-red/10 px-3 py-2 text-sm text-status-red">
          <span className="font-bold">Platzverbot (Abgleich): </span>
          {booking.place_ban_reason}
        </div>
      ) : null}

      {guest?.notes ? (
        <p className="mt-3 border-t border-separator pt-3 text-sm leading-relaxed text-label-secondary whitespace-pre-wrap">
          <span className="font-medium text-label">Gastnotizen: </span>
          {guest.notes}
        </p>
      ) : null}
    </section>
  );
}
